import React, { CSSProperties } from 'react';
import { Box, Typography, useMediaQuery } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import ChatBubbleOutline from '@mui/icons-material/ChatBubbleOutline';
import { useI18n } from '../../../lib/hook/useI18n';
import ThemableButton, {
  ThemableButtonProps,
} from '../../../theme/components/Button/ThemableButton';

type ButtonProps = Pick<
  ThemableButtonProps,
  | 'classes'
  | 'className'
  | 'disabled'
  | 'fullWidth'
  | 'onClick'
  | 'styles'
  | 'variant'
>;

interface ConversationButtonProps extends ButtonProps {
  disableBreakPoint?: boolean;
  /**
   * Whether the button is just a quick access icon that summarizes the
   * number of messages in the conversation and allows to open the conversation.
   * Used in the room's header in mobile breakpoint.
   */
  isQuickAccessIcon?: boolean;
  messagesCount?: number;
}

/**
 * Button for the room's conversation, with different layouts:
 * - [icon] "Start a conversation": room create/edit/preview, tablet & desktop headers, mobile menu (if no messages)
 * - [icon] "Conversation" [counter]: tablet & desktop headers (if messages)
 * - [icon] [counter]: mobile header
 * - [icon]: mobile header with no messages
 */
const ConversationButton: React.FC<ConversationButtonProps> = (
  props: ConversationButtonProps
) => {
  const { t } = useI18n();
  const theme = useTheme();

  const {
    disableBreakPoint = false,
    isQuickAccessIcon = false,
    messagesCount = 0,
    styles,
    variant = 'text',
    ...butttonProps
  } = props;

  const mediaQuery = useMediaQuery(theme.breakpoints.down('sm'));
  const smDown = !disableBreakPoint && mediaQuery;

  const textLabel =
    messagesCount > 0
      ? t('conversation.messages_count', { counter: messagesCount })
      : t('conversation.start_conversation');

  const size = smDown ? 'large' : 'small';
  const icon = <ChatBubbleOutline fontSize={smDown ? 'medium' : 'small'} />;

  let label = smDown ? icon : textLabel;
  if (smDown && isQuickAccessIcon && messagesCount > 0) {
    label = (
      <Box alignItems="center" display="flex">
        {icon}
        <Typography color="textSecondary" sx={{ ml: 1 }} variant="caption">
          <Box component="strong">({messagesCount})</Box>
        </Typography>
      </Box>
    );
  }

  let mobileStyles: CSSProperties | undefined;
  if (smDown || isQuickAccessIcon) {
    mobileStyles = {
      color: 'var(--gray-color)',
      minWidth: 'unset',
      padding: theme.spacing(1.5),
    };
  }

  return (
    <ThemableButton
      size={size}
      startIcon={smDown ? undefined : icon}
      styles={{
        ...styles,
        ...mobileStyles,
        textTransform: 'none',
      }}
      variant={variant}
      {...butttonProps}
      dataTestId="button-start-conversation"
    >
      {label}
    </ThemableButton>
  );
};

export default ConversationButton;
